import { z } from "zod";

import { isPastDateTime } from "../date";
import { isObject } from "../type-guard";

const zErrorCode = z.enum(["DATE_TIME_MUST_NOT_BE_IN_THE_PAST"]);
const ErrorCode = zErrorCode.Enum;
type ErrorCode = z.infer<typeof zErrorCode>;

const INVALID_DATE_TIME = "invalid_date_time";
const CUSTOM_CODE = "customCode";

type InvalidDateTimeIssue = Omit<z.ZodCustomIssue, "params"> & {
  params: {
    [CUSTOM_CODE]: typeof INVALID_DATE_TIME;
    errorCode: ErrorCode;
  };
};

function isInvalidDateTimeIssue(value: z.ZodIssueOptionalMessage): value is InvalidDateTimeIssue {
  return (
    value.code === z.ZodIssueCode.custom &&
    isObject(value.params) &&
    value.params[CUSTOM_CODE] === INVALID_DATE_TIME
  );
}

const CONFIG: Record<
  ErrorCode,
  {
    check: (value: Date) => boolean;
    errorMessage: string;
  }
> = {
  DATE_TIME_MUST_NOT_BE_IN_THE_PAST: {
    check: (value) => !isPastDateTime(value),
    errorMessage: "The date cannot be earlier than the current moment",
  },
};

function refine(errorCode: ErrorCode) {
  const { check } = CONFIG[errorCode];
  return (value: Date, ctx: z.RefinementCtx) => {
    if (!check(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        params: {
          customCode: INVALID_DATE_TIME,
          errorCode,
        } satisfies InvalidDateTimeIssue["params"],
      });
    }
  };
}

const REFINEMENTS = {
  notInThePast: refine(ErrorCode.DATE_TIME_MUST_NOT_BE_IN_THE_PAST),
};

const errorMap: z.ZodErrorMap = (issue, ctx) => {
  if (isInvalidDateTimeIssue(issue)) {
    return {
      message: CONFIG[issue.params.errorCode].errorMessage,
    };
  }

  return {
    message: ctx.defaultError,
  };
};

export { ErrorCode, errorMap, isInvalidDateTimeIssue, REFINEMENTS };
export type { InvalidDateTimeIssue };
